import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigate, useSearchParams } from '@remix-run/react';

import ActionForm from '~/components/ActionForm';
import { useComponentFetcher } from '~/components/ComponentErrorBoundary/useComponentFetcher';
import StatusInputs from '~/components/Forms/StatusInputs';
import useEmail from '~/hooks/useEmail';
import useToast from '~/hooks/useToast';
import useSignupRedirects from '~/hooks/useUtils/useSignupRedirects';

interface Props {
  hasEmail: boolean;
  email?: string;
  id: string;
  publicationId: string;
  successMessage?: string;
  children: (props: { isLoading: boolean; isSuccess: boolean; submittingText: string }) => ReactNode;
}

const SurveyProvider = ({ hasEmail, email, id, publicationId, successMessage, children }: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const fetcher = useComponentFetcher();
  const fetcherData: any = fetcher.data;
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { nextPage } = useSignupRedirects();
  const formRef = useRef<HTMLFormElement>(null);

  const submittingText = `${t('shared.actions.submitting')}...`;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setIsLoading(true);

    const formData = new FormData(e.currentTarget);
    fetcher.submit(formData, {
      method: 'post',
      action: '/post_subscribe_form',
    });
  };

  const handleSuccess = (data: any) => {
    if (data?.redirect) {
      navigate('/');
      return;
    }

    formRef.current?.reset();
    setIsLoading(false);
    setIsSuccess(true);
    nextPage();
  };

  const handleError = (data: any) => {
    if (data?.redirect) {
      navigate('/');
      return;
    }

    setIsLoading(false);
  };

  // A small UX improvement to check for an email in local storage and pre-populate the form
  const [, setSearchParams] = useSearchParams();
  const [localStorageEmail] = useEmail();
  useEffect(() => {
    if (!email && localStorageEmail) {
      setSearchParams({ email: localStorageEmail });
    }
  }, [localStorageEmail, setSearchParams, email]);

  useToast(fetcherData?.toast);

  return (
    <ActionForm
      fetcher={fetcher}
      formRef={formRef}
      method="post"
      action="/post_subscribe_form"
      onSubmit={(e) => {
        setIsLoading(true);
        handleSubmit(e);
      }}
      onSuccess={(data) => {
        handleSuccess(data);
      }}
      onError={(data) => {
        handleError(data);
      }}
    >
      {hasEmail && <input name="email" type="hidden" value={email} />}
      <input name="form_id" type="hidden" value={id} />
      <input name="publication_id" type="hidden" value={publicationId} />
      <StatusInputs name="no_subscription" errorMessage={t('forms.toast.no_subscription')} successMessage={''} />
      <StatusInputs
        name="form_response"
        errorMessage={t('errors.default')}
        successMessage={successMessage || t('forms.toast.success')}
      />
      {children({ isLoading, isSuccess, submittingText })}
    </ActionForm>
  );
};

export default SurveyProvider;
